import React, {useCallback, useMemo, useState} from 'react';
import {AvailabilityUI} from '../../common/ui/AvailabilityUI';
import {ProductPriceUI} from './ProductPriceUI';
import {DealSelector} from './DealSelector';
import {PriceBreakdownUI} from './PriceBreakdownUI';
import {NumberInputUI} from '../../ui/components/NumberInputUI';
import {useProductSelection} from '../application/useProductSelection';
import {useCartContext} from '../../cart';
import {toast} from 'react-toastify';
import {AddUpdateButton} from '../../common/ui/AddUpdateButton';
import {DealType} from '../domain/DealSelection.model';
import {TrackProductButton} from "../../common/ui/TrackProductButton";
import {useTrackingProduct} from "../application/useTrackingProduct";

/**
 * @typedef {Object} PurchasePanelProps
 * @property {Object} product
 * @property {number} defaultQuantity
 * @property {() => void} [afterAddToCart]
 */

const isAddButtonDisabled = (productSelection, fetching) => {
    const { price, quantity, availability, dealType } = productSelection;
    return (
        price?.sellingPrice <= 0 ||
        quantity <= 0 ||
        fetching ||
        (dealType === DealType.LOT && availability.isAvailable === false)
    );
};

const handleAddToCart = async ({ setFetching, addProduct, afterAddToCart, productSelection, product }) => {
    try {
        setFetching(true);
        await addProduct(productSelection);
    toast.success(`'${product.name || product.code}' agregado al carrito exitosamente.`)
    } catch (error) {
    toast.error(`'${product.name || product.code}' no pudo agregarse al carro. Por favor, reintente más tarde o contacte con nosotros.`)
    } finally {
        afterAddToCart();
        setFetching(false);
    }
};

export const PurchasePanel = ({ product, defaultQuantity, afterAddToCart = () => null }) => {
    const {
        productSelection,
        dealSelection,
        setDealSelection,
        setQuantity
    } = useProductSelection(product, defaultQuantity);

    const { isOnCart, addProduct } = useCartContext();
    const { isTracked, handleTrack, handleUntrack } = useTrackingProduct(product);
    const [fetching, setFetching] = useState(false);

    const disabled = useMemo(() => isAddButtonDisabled(productSelection, fetching), [productSelection, fetching]);
    const hasPrice = useMemo(() => Boolean(product.price), [product.price]);
    const hasStock = useMemo(() => (product.availableUnits > 0 || product.onDemand), [product.availableUnits, product.onDemand]);

    const onAddToCart = useCallback(() => {
        handleAddToCart({ setFetching, addProduct, afterAddToCart, productSelection, product });
    }, [setFetching, addProduct, afterAddToCart, productSelection, product]);

    const renderTrackProductButton = () => (
        <TrackProductButton
            isTracked={isTracked}
            onTrack={() => handleTrack(
                () => toast.success("Producto agregado a tus productos pendientes!"),
                () => toast.error("Error al agregar producto a tus productos pendientes")
            )}
            onUntrack={() => handleUntrack(
                () => toast.success("Producto agregado a tus productos pendientes!"),
                () => toast.error("Error al agregar producto a tus productos pendientes")
            )}
        />
    );

    const renderPriceSection = () => (
        <>
            <div className="mb-2 d-flex flex-row align-items-bottom gap-2">
                <AvailabilityUI data={{ availability: productSelection.availability }} />
            </div>
            <div className="d-flex flex-row justify-content-start align-items-end gap-2">
                <div className="d-flex align-items-center gap-2">
                    {!!productSelection.price && <span>Precio Unidad: </span>}
                    <ProductPriceUI price={productSelection.price} />
                </div>
            </div>
            {!!productSelection.discount?.text && (
                <p>
                    Porcentaje de ahorro: <strong className="text-primary">{productSelection.discount.text}</strong>
                </p>
            )}
        </>
    );

    const renderDealSelector = () => (
        product?.hasDealsToChoose && (
            <>
                <hr />
                <DealSelector
                    product={product}
                    selection={dealSelection}
                    onSelectionChange={setDealSelection}
                />
            </>
        )
    );

    const renderPurchaseControls = () => (
        <div className="row justify-content-end align-items-center gap-2 gap-sm-0">
            <div className="col-auto">
                <PriceBreakdownUI
                    price={{
                        sellingPrice: productSelection.price.sellingPrice * productSelection.quantity,
                        basePrice: productSelection.price.basePrice * productSelection.quantity
                    }}
                    discount={productSelection.discount}
                />
            </div>
            <div className="col-auto">
                <NumberInputUI
                    data={{ value: productSelection.quantity }}
                    actions={{ setValue: setQuantity }}
                />
            </div>
            <div className="col-auto">
                <AddUpdateButton
                    shouldAdd={isOnCart(product.id)}
                    disabled={disabled || !hasStock}
                    loading={fetching}
                    onClick={onAddToCart}
                />
            </div>
            {(isTracked || !hasStock) && (
                <div className="col-auto">
                    {renderTrackProductButton()}
                </div>
            )}
        </div>
    );

    const renderUnavailableInfo = () => (
        <div className="d-flex flex-row align-items-center justify-content-between gap-2">
            {!hasPrice && <span className="text-warning">Precio online no disponible</span>}
            {renderTrackProductButton()}
        </div>
    );

    return (
        <div className="shadow-sm p-2">
            {hasPrice ? (
                <>
                    {renderPriceSection()}
                    {renderDealSelector()}
                    <hr />
                    {renderPurchaseControls()}
                </>
            ) : (
                renderUnavailableInfo()
            )}
        </div>
    );
};
